import { Big } from 'big.js';
import { TFunction } from 'i18next';
import identity from 'lodash.identity';
import isEqual from 'lodash.isequal';
import sortBy from 'lodash.sortby';
import { match } from 'ts-pattern';
import { ValueOf } from 'ts-pattern/dist/types/helpers';
import { DomainPreferences } from './api/types';
import { translateLabel } from './common/utils';
import {
  $UndefinedValueIndicator$,
  ASCII_ARROW,
  DEFAULT_OPTION,
  HierarchicalFields,
  UNDEFINED_DISPLAY_KEY,
} from './constants';
import {
  ApiMasterDataQueryFilterItem,
  ApplicationDataFields,
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  FnvHashCode,
  FormatTypes,
  HashCode,
  NonEmptyArray,
  ValueTypes,
} from './types';

export const hierarchicalFieldFullLabel = (
  t: TFunction,
  values: (string | null)[],
  dimension: DataFieldWithDataType,
  defaultValue: string
) => {
  const label =
    values
      .map((v: string | null) => {
        const translatedLabel = translateLabel(v, dimension);
        return translatedLabel
          ? (t(translatedLabel, { defaultValue: UNDEFINED_DISPLAY_KEY }) ??
              defaultValue)
          : v;
      })
      .join(ASCII_ARROW) ?? defaultValue;
  if (label.length === 0) {
    return defaultValue;
  } else {
    return label;
  }
};

export const hierarchicalFieldLabel = (
  t: TFunction,
  value: string | null,
  dimension: DataFieldWithDataType,
  defaultValue: string
) => {
  const translatedLabel = translateLabel(value, dimension);
  const label = translatedLabel
    ? t(translatedLabel, { defaultValue })
    : (value ?? defaultValue);
  return label;
};

export const nonHierarchicalFieldLabel = (
  t: TFunction,
  value: string | null,
  dimension: DataFieldWithDataType,
  defaultValue: string
) => {
  const translatedLabel = translateLabel(value, dimension);
  return translatedLabel
    ? t(translatedLabel, { defaultValue })
    : (value?.toString() ?? defaultValue);
};

// Source: https://stackoverflow.com/a/7616484
export const hashCode = (s: string): HashCode => {
  let hash = 0,
    i,
    chr;
  if (s.length === 0) return { type: 'hashCode', hash };
  for (i = 0; i < s.length; i++) {
    chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return { type: 'hashCode', hash: Math.abs(hash) };
};

/**
 * Implements the FNV-1a hash algorithm for 64-bit integers
 * Returns a 64-bit hash value for better collision resistance
 *
 * Advantages of FNV-1a:
 * - Better Distribution: FNV-1a is designed to have excellent distribution of hash values, reducing the likelihood of collisions.
 * - Avalanche Effect: Small changes in the input string will cause significant changes in the output hash, making it better for detecting differences.
 * - 64-bit Internal Calculation: While we still return a 32-bit integer for compatibility, the internal calculations use 64-bit integers, which provides better intermediate precision.
 *
 * The collision probability for the fnvHash function is extremely low:
 * With 1 million unique strings: ~0.0000000001% chance of collision
 */
export const fnvHash = (s: string): FnvHashCode => {
  if (!s || s.length === 0) {
    return { type: 'fnvHashCode', hash: 0n };
  }
  const FNV_PRIME = 0x100000001b3n;
  const FNV_OFFSET = 0xcbf29ce484222325n;
  let hash = FNV_OFFSET;

  for (let i = 0; i < s.length; i++) {
    hash = hash ^ BigInt(s.charCodeAt(i));
    hash = (hash * FNV_PRIME) & 0xffffffffffffffffn; // Keep as 64-bit integer
  }

  return { type: 'fnvHashCode', hash };
};

export function enumKeys<O extends object, K extends keyof O = keyof O>(
  obj: O
): K[] {
  return Object.keys(obj).filter((k) => Number.isNaN(+k)) as K[];
}

// Iterate through enumeration keys without loosing the type of the enumeration
// Without this, the type of each element would be string
export function enumValues<O extends object, V extends ValueOf<O> = ValueOf<O>>(
  obj: O
): V[] {
  return Object.values(obj) as V[];
}

export function objectKeys<T extends object>(obj: T): Array<keyof T> {
  // Use a type assertion to inform the compiler
  return Object.keys(obj) as Array<keyof T>;
}

export const toDataFieldWithDataType = (
  dataType: DataTypes,
  field: string
) => ({
  dataType,
  dataField: field as DataFields,
});

// nonFrontendQueryableFields are those that won't be used in queries
// from the frontend and therefore don't need permissions to be set on them
export const internalFields: Set<DataFieldWithDataType> = new Set([
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.PERMISSIONS },
  { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.SUPERIOR_IDS },
  ...enumValues(DataTypes).flatMap((d) => {
    return [
      toDataFieldWithDataType(d, 'ID'),
      toDataFieldWithDataType(d, 'ENTITY_ID'),
      toDataFieldWithDataType(d, 'IDHASH'),
      toDataFieldWithDataType(d, 'NAMESPACE'),
    ];
  }),
]);

export const nonFrontendQueryableFields: Set<DataFieldWithDataType> = new Set([
  ...Array.from(internalFields),
  ...HierarchicalFields,
]);

// UserVisible fields are those that are meant to be shown on dataview, column selector, etc
// excludes fields like EFFECTIVE_LEAVER_DATE_NORMALIZED, DATE_OF_BIRTH_YEAR_MONTH, etc
// which are used in queries internally
export const queryableButNonUserVisibleFields: Set<DataFieldWithDataType> =
  new Set([
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.EMPLOYMENT_TEMPORALITY,
    },
    { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.VERSION_ID },
    { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.SUPERIOR_ID },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.START_DATE_NORMALIZED,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.TERM_DATE_NORMALIZED,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.EFFECTIVE_LEAVER_DATE_NORMALIZED,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.EFFECTIVE_LEAVER_DATE_NORMALIZED_YEAR_MONTH,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.START_DATE_NORMALIZED_YEAR_MONTH,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.TERM_DATE_NORMALIZED_YEAR_MONTH,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.DATE_OF_BIRTH_YEAR_MONTH,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.CALENDAR_YEAR,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.CALENDAR_QUARTER,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FINANCIAL_YEAR,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FINANCIAL_QUARTER,
    },
    {
      dataType: DataTypes.APPLICATION,
      dataField: ApplicationDataFields.VERSION_ID,
    },
  ]);

export const nonUserVisibleFields = new Set([
  ...Array.from(queryableButNonUserVisibleFields),
  ...Array.from(nonFrontendQueryableFields),
]);

export const isValidNumber = (val: number | string) => {
  if (val === 0 || val === '0') {
    return true;
  } else {
    return Boolean(Number(val));
  }
};

interface ValueLimitToPrecisionMap {
  1: number;
  10: number;
  100: number;
  1000: number;
  [DEFAULT_OPTION]: number;
}

const formatTypeToPrecisionsMap: Record<
  FormatTypes,
  ValueLimitToPrecisionMap | null
> = {
  [FormatTypes.PERCENTAGE]: {
    1: 2,
    10: 2,
    100: 1,
    1000: 0,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.COUNT]: {
    1: 0,
    10: 0,
    100: 0,
    1000: 0,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.NUMBER]: {
    1: 2,
    10: 1,
    100: 0,
    1000: 0,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.CURRENCY_INDIVIDUAL]: {
    1: 2,
    10: 2,
    100: 2,
    1000: 2,
    [DEFAULT_OPTION]: 2,
  },
  [FormatTypes.CURRENCY_AGGREGATE]: {
    1: 2,
    10: 2,
    100: 1,
    1000: 1,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.SENSITIVE_NUMBER]: {
    1: 2,
    10: 1,
    100: 1,
    1000: 0,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.HOURS]: {
    1: 2,
    10: 2,
    100: 1,
    1000: 1,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.LEAVES_TAKEN]: {
    1: 2,
    10: 2,
    100: 1,
    1000: 1,
    [DEFAULT_OPTION]: 0,
  },
  [FormatTypes.FTE]: {
    1: 2,
    10: 2,
    100: 2,
    1000: 2,
    [DEFAULT_OPTION]: 2,
  },
  [FormatTypes.EXACT_NUMBER]: null,
  [FormatTypes.FLOATING_NUMBER_1]: {
    1: 1,
    10: 1,
    100: 1,
    1000: 1,
    [DEFAULT_OPTION]: 1,
  },
  [FormatTypes.FLOATING_NUMBER_2]: {
    1: 2,
    10: 2,
    100: 2,
    1000: 2,
    [DEFAULT_OPTION]: 2,
  },
  [FormatTypes.FLOATING_PERCENTAGE_1]: {
    1: 1,
    10: 1,
    100: 1,
    1000: 1,
    [DEFAULT_OPTION]: 1,
  },
  [FormatTypes.FLOATING_PERCENTAGE_2]: {
    1: 2,
    10: 2,
    100: 2,
    1000: 2,
    [DEFAULT_OPTION]: 2,
  },
};

export const getFormattingPrecisionForNumber = (
  num: number,
  formatType: FormatTypes
) => {
  const absNum = Math.abs(num);
  const precisionsMap = formatTypeToPrecisionsMap[formatType];
  if (precisionsMap === null) {
    return null;
  } else {
    if (absNum < 1) {
      return precisionsMap[1];
    } else if (absNum < 10) {
      return precisionsMap[10];
    } else if (absNum < 100) {
      return precisionsMap[100];
    } else if (absNum < 1000) {
      return precisionsMap[1000];
    } else {
      return precisionsMap[DEFAULT_OPTION];
    }
  }
};

export const roundOffNumber = (
  num: number | string,
  formatType: FormatTypes
): number => {
  const n = Number(num);
  const precision = getFormattingPrecisionForNumber(n, formatType);
  if (precision !== null) {
    return Number(toFixedWithPrecision(n, precision));
  } else {
    return n;
  }
};

export const ratio = (numerator: number, denominator: number) => {
  if (!numerator || !denominator) return 0;
  const ratio =
    denominator > 0
      ? (isValidNumber(numerator) ? Number(numerator) : 0) / denominator
      : 0;
  return ratio;
};

export const percentage = (numerator: number, denominator: number) => {
  return ratio(numerator * 100, denominator);
};

export const currencySymbol: Record<string, string> = {
  JPY: '円',
  USD: '$',
  EUR: '€',
  MXN: 'MXN',
  AUD: 'A$',
};

export const filterToDataFieldWithDataType = (
  filter: ApiMasterDataQueryFilterItem
) => ({
  dataType: filter.dataType,
  dataField: filter.property as DataFields,
});

export const getKeyFromDataFieldWithDataType = (
  d: DataFieldWithDataType
): string => {
  return `${d.dataType}+__+${d.dataField}`;
};

export const getDataFieldWithDataTypeFromKey = (
  d: string
): DataFieldWithDataType => {
  const [dataType, dataField] = d.split('+__+');
  return {
    dataType: dataType as DataTypes,
    dataField: dataField as DataFields,
  };
};

export const findWithRest =
  <T>(args: T[]) =>
  (cond: (t: T) => boolean): NonEmptyArray<T> => {
    return [args.find(cond), ...args.filter((t) => !cond(t))];
  };

export const toFixedWithPrecision = (val: number, precision: number) => {
  // Check this answer for why Big library is necessary
  // https://stackoverflow.com/a/58968649
  // Without this, 0.15.toFixed(1) gets converted to 0.1 instead of 0.2
  // This is because 0.15 in floating point representation is stored as 0.1499999....
  const res = Big(val).toFixed(precision);
  // wrapping in number and then string to show "-0" as "0"
  return res === '-0' ? '0' : res;
};

export const replaceSpaces = (s: string | null, replacement = '_') => {
  if (s === null) {
    return s;
  }
  return s.replace(/\s/g, replacement);
};

export const getSortIndices = <T>(
  formattedValues: T[],
  sort: (
    v: { label: T; index: number }[]
  ) => { label: string; index: number }[],
  nullIndex?: number
): number[] => {
  const valuesWithIndex = formattedValues.map((v, i) => ({
    label: v,
    index: i,
  }));
  const sorted = sort(valuesWithIndex);
  const hasNull = nullIndex !== undefined && nullIndex >= 0;
  const newIndices = hasNull
    ? [
        ...sorted
          .map((item) => item.index)
          .filter((index) => index !== nullIndex),
        nullIndex,
      ]
    : sorted.map((item) => item.index);
  return newIndices;
};

export const sortByIndices = <T>(
  arr: T[],
  indices: number[],
  reverse = false
) => {
  const result = indices.map((i) => arr[i]);
  return reverse ? result.reverse() : result;
};

export const sortValuesAlphabetically = <T extends { label: string | null }>(
  values: T[],
  asc = true
) => {
  return [...values].sort((a, b) => {
    const isAUndefined =
      a.label === null || a.label.includes($UndefinedValueIndicator$);
    const isBUndefined =
      b.label === null || b.label.includes($UndefinedValueIndicator$);
    if (isAUndefined && isBUndefined) return 0;
    if (isAUndefined) return 1;
    if (isBUndefined) return -1;

    // Check if both values are numeric strings
    const aNum = Number(a.label);
    const bNum = Number(b.label);
    const isANumber = !isNaN(aNum);
    const isBNumber = !isNaN(bNum);

    // If both are numbers, sort numerically
    if (isANumber && isBNumber) {
      return asc ? aNum - bNum : bNum - aNum;
    }

    // Otherwise, use alphabetical sorting
    return asc
      ? String(a.label).localeCompare(String(b.label))
      : String(b.label).localeCompare(String(a.label));
  });
};

export const sortFieldValues = <T extends { label: string | null }>(
  values: T[],
  dataField: DataFieldWithDataType,
  preferences: DomainPreferences,
  asc = true
) => {
  // Sort using sortHierarchy
  const sorter = [...(fieldSorter(dataField, preferences) ?? [])];
  if (sorter.length > 0) {
    if (!asc) {
      sorter.reverse();
    }
    return sortBy(values, (i) => {
      const index = sorter.indexOf(i.label);
      if (index !== -1) {
        return index;
      }
      // Unmatched items: non-null labels come before null labels
      return i.label === null ? sorter.length + 1 : sorter.length;
    });
  } else {
    // Fallback to simple alphabetical sort if no segmentations
    return sortValuesAlphabetically(values, asc);
  }
};

export const fieldSorter = (
  dataField: DataFieldWithDataType,
  preferences: DomainPreferences
): (string | null)[] | null => {
  // Handle hierarchical fields internally
  const effectiveField = isHierarchical(dataField)
    ? {
        ...dataField,
        dataField: `${dataField.dataField}_LEVEL_1` as DataFields,
      }
    : dataField;
  const sortOrder = match(effectiveField)
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.TENURE_GROUP,
      },
      () => preferences.tenureGroups?.map((t) => t.description)
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.CUSTOM_TENURE_GROUP,
      },
      () => preferences.tenureGroups?.map((t) => t.description)
    )
    .with(
      { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.AGE_GROUP },
      () => preferences.ageGroups?.map((t) => t.description)
    )
    .with({ dataType: DataTypes.JOINERS_VIEW }, () => {
      const field = { ...dataField, dataType: DataTypes.EMPLOYEE };
      return preferences.sortSettings?.find((s) => isEqual(s.field, field))
        ?.sortOrder;
    })
    .otherwise(
      () =>
        preferences.sortSettings?.find((s) => {
          return isEqual(s.field, effectiveField);
        })?.sortOrder
    );
  return sortOrder ?? null;
};

export const sortBySorterArray = <T, V>(
  values: T[],
  sorterArray: readonly V[],
  getValueToSortBy: (val: T) => V = identity,
  asc = true
) => {
  const newValues = values.sort((a, b) => {
    const aValue = getValueToSortBy(a);
    const bValue = getValueToSortBy(b);
    if (sorterArray.indexOf(aValue) === -1) {
      return asc ? 1 : -1;
    } else if (sorterArray.indexOf(bValue) === -1) {
      return asc ? -1 : 1;
    } else {
      return asc
        ? sorterArray.indexOf(aValue) - sorterArray.indexOf(bValue)
        : sorterArray.indexOf(bValue) - sorterArray.indexOf(aValue);
    }
  });
  return newValues;
};

/**
 * Limit `value` between `min` and `max`
 * @param value
 * @param min
 * @param max
 * @private
 */
export const limitValue = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

export const getUndefinedValue = (t: TFunction) =>
  `${$UndefinedValueIndicator$}${t(UNDEFINED_DISPLAY_KEY)}`;

export const getDisplayValueForMaybeUndefinedValue = (value: string) => {
  if (value.includes($UndefinedValueIndicator$)) {
    return value.split($UndefinedValueIndicator$)[1];
  } else {
    return value;
  }
};

export const sum = (array: number[]) =>
  array.reduce((acc, curr) => acc + (curr ?? 0), 0);

export const isHierarchical = (
  filterProperty: DataFieldWithDataType
): boolean => {
  return Array.from(HierarchicalFields).some((e) => isEqual(filterProperty, e));
};

/**
 * Type guard to check if a value is a valid ValueTypes
 * ValueTypes can be:
 * - A single value: string | number | null (NonHierarchicalValueTypes)
 * - An array of single values: (string | number | null)[] (HierarchicalValueTypes)
 */
export const isValueTypes = (value: unknown): value is ValueTypes => {
  // Check if it's a single value (string, number, or null)
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number'
  ) {
    return true;
  }
  // Check if it's an array of single values
  if (Array.isArray(value)) {
    return value.every(
      (item) =>
        item === null || typeof item === 'string' || typeof item === 'number'
    );
  }
  return false;
};

export interface SuspenseResource<T> {
  read: () => T;
}
export const wrapPromise = <T>(promise: Promise<T>): SuspenseResource<T> => {
  type Status = 'pending' | 'success' | 'error';
  let status: Status = 'pending';
  let value: T | undefined;
  let error: unknown;

  promise.then(
    (res: T) => {
      status = 'success';
      value = res;
    },
    (err: unknown) => {
      status = 'error';
      error = err;
    }
  );

  return {
    read: () => {
      switch (status) {
        case 'pending':
          throw promise;
        case 'error':
          throw error;
        default:
          return value as T;
      }
    },
  };
};
